import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Title from "../../components/Title";
import Loader from "../../components/Loader";
import useScrollToTop from "../../hooks/useScrollToTop";
import { getRegistrationForm } from "../../api/voiceTalents/getRegistrationForm";
import { registerVoiceTalent } from "../../api/voiceTalents/registerVoiceTalent";
import SearchableSelect from "../../components/SearchableSelect";

const CHOICE_SINGLE = ["select", "radio", "country", "age"];
const CHOICE_MULTI = ["multiselect", "checkbox"];
// Audio uploads are handled by the dedicated samples section below.
const SKIP_TYPES = ["file", "audio"];
// System key of the performance-types field (mirrors the backend). Every
// selected performance type must be backed by at least one voice sample.
const PERFORMANCE_KEY = "performance_types";

// Used until the server reports its real limits, so the hint is never blank.
const FALLBACK_CONSTRAINTS = {
  max_size_mb: 20,
  max_size_kb: 20480,
  accepted_extensions: ["mp3", "wav", "m4a", "ogg"],
};

// Samples need a stable identity: they can be removed from anywhere in the
// list, and a native file input keeps its own DOM state, so reusing the array
// index as a React key would leave the wrong file attached to the wrong row.
let sampleCounter = 0;
const emptySample = () => ({
  uid: `sample-${++sampleCounter}`,
  file: null,
  title: "",
  performanceType: "",
  // Bumped to remount (and therefore clear) the native file input.
  fileNonce: 0,
});

const RegisterVoiceTalent = () => {
  const lng = useSelector((state) => state.lang.value);
  const { t } = useTranslation();
  useScrollToTop();

  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [performanceOptions, setPerformanceOptions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [samples, setSamples] = useState([emptySample()]);
  const [constraints, setConstraints] = useState(FALLBACK_CONSTRAINTS);
  // Files rejected in the browser, keyed by sample uid, so the applicant is
  // told immediately instead of after a failed upload.
  const [sampleIssues, setSampleIssues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  const errorBoxRef = useRef(null);
  const samplesRef = useRef(null);

  useEffect(() => {
    getRegistrationForm()
      .then((data) => {
        setFields(data.fields || []);
        setPerformanceOptions(data.performance_type_options || []);
        if (data.sample_constraints) {
          setConstraints({ ...FALLBACK_CONSTRAINTS, ...data.sample_constraints });
        }
      })
      .catch((error) => console.error("Error fetching form", error))
      .finally(() => setLoading(false));
  }, []);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleMulti = (key, optionValue) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: current.includes(optionValue)
          ? current.filter((v) => v !== optionValue)
          : [...current, optionValue],
      };
    });
  };

  const updateSample = (uid, patch) => {
    setSamples((prev) =>
      prev.map((sample) => (sample.uid === uid ? { ...sample, ...patch } : sample))
    );
  };

  const addSample = () => setSamples((prev) => [...prev, emptySample()]);

  const forgetIssue = (uid) =>
    setSampleIssues((prev) => {
      if (!(uid in prev)) return prev;
      const next = { ...prev };
      delete next[uid];
      return next;
    });

  const removeSample = (uid) => {
    forgetIssue(uid);
    setSamples((prev) => prev.filter((sample) => sample.uid !== uid));
  };

  const clearSampleFile = (uid) => {
    forgetIssue(uid);
    setSamples((prev) =>
      prev.map((sample) =>
        sample.uid === uid
          ? { ...sample, file: null, fileNonce: sample.fileNonce + 1 }
          : sample
      )
    );
  };

  const acceptedExtensions = constraints.accepted_extensions || [];
  const maxSizeMb = constraints.max_size_mb;
  const maxSizeBytes = (constraints.max_size_kb || 0) * 1024;

  const sizeHint = `${t("Supported formats:")} ${acceptedExtensions
    .join(", ")
    .toUpperCase()} — ${t("maximum size")} ${maxSizeMb} ${t("MB")}`;

  const fileAccept = ["audio/*"]
    .concat(acceptedExtensions.map((extension) => `.${extension}`))
    .join(",");

  // Rejecting a file here saves the applicant a long upload that the server
  // would refuse anyway (or that PHP would drop before Laravel even runs).
  const fileProblem = (file) => {
    if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
      return `${t("This file is too large. Maximum size is")} ${maxSizeMb} ${t(
        "MB"
      )}.`;
    }

    const extension = (file.name.split(".").pop() || "").toLowerCase();
    const looksLikeAudio =
      acceptedExtensions.includes(extension) ||
      String(file.type || "").startsWith("audio/");

    if (!looksLikeAudio) {
      return `${t("Unsupported file type.")} ${sizeHint}`;
    }

    return null;
  };

  const selectedPerformanceTypes = Array.isArray(answers[PERFORMANCE_KEY])
    ? answers[PERFORMANCE_KEY]
    : answers[PERFORMANCE_KEY]
    ? [answers[PERFORMANCE_KEY]]
    : [];

  // Attaching a file is the user's intent to cover a performance type, so the
  // type is filled in automatically (first one still uncovered). It stays
  // editable, which keeps the "one sample per selected type" rule reachable
  // without the applicant having to discover a second dropdown.
  const handleSampleFile = (uid, file) => {
    const problem = file ? fileProblem(file) : null;

    if (problem) {
      setSampleIssues((prev) => ({ ...prev, [uid]: problem }));
      setSamples((prev) =>
        prev.map((sample) =>
          sample.uid === uid
            ? { ...sample, file: null, fileNonce: sample.fileNonce + 1 }
            : sample
        )
      );
      return;
    }

    forgetIssue(uid);

    setSamples((prev) => {
      const next = prev.map((sample) =>
        sample.uid === uid ? { ...sample, file } : sample
      );

      const target = next.find((sample) => sample.uid === uid);
      if (
        !file ||
        !target ||
        target.performanceType ||
        selectedPerformanceTypes.length === 0
      ) {
        return next;
      }

      const takenElsewhere = new Set(
        next
          .filter(
            (sample) =>
              sample.uid !== uid && sample.file && sample.performanceType
          )
          .map((sample) => sample.performanceType)
      );
      const suggested =
        selectedPerformanceTypes.find((value) => !takenElsewhere.has(value)) ||
        selectedPerformanceTypes[0];

      return next.map((sample) =>
        sample.uid === uid ? { ...sample, performanceType: suggested } : sample
      );
    });
  };

  const coveredTypes = new Set(
    samples
      .filter((sample) => sample.file && sample.performanceType)
      .map((sample) => sample.performanceType)
  );

  const missingTypes = selectedPerformanceTypes.filter(
    (value) => !coveredTypes.has(value)
  );

  const performanceLabel = (value) => {
    const option = performanceOptions.find((o) => o.value === value);
    return option ? option.label[lng] : value;
  };

  // A sample can only count towards a type the applicant actually selected, so
  // offer exactly those; otherwise the choice would silently not count.
  const samplePerformanceOptions = useMemo(() => {
    const source =
      selectedPerformanceTypes.length > 0
        ? performanceOptions.filter((option) =>
            selectedPerformanceTypes.includes(option.value)
          )
        : performanceOptions;

    return source.map((option) => ({
      value: option.value,
      label: option.label[lng],
    }));
  }, [performanceOptions, selectedPerformanceTypes, lng]);

  // Every validation message, whatever its key, so nothing (including
  // per-file upload errors) can fail silently.
  const errorList = useMemo(
    () =>
      Object.values(errors).flatMap((messages) =>
        (Array.isArray(messages) ? messages : [messages]).map((m) => String(m))
      ),
    [errors]
  );

  const focusErrors = () => {
    if (errorBoxRef.current) {
      errorBoxRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess("");

    const rejectedFiles = Object.values(sampleIssues);

    if (rejectedFiles.length > 0) {
      setErrors({ samples: rejectedFiles });
      setTimeout(focusErrors, 50);
      return;
    }

    if (missingTypes.length > 0) {
      setErrors({
        [`answers.${PERFORMANCE_KEY}`]: [
          `${t(
            "Please add a voice sample for each selected performance type, or remove it."
          )} (${missingTypes.map(performanceLabel).join("، ")})`,
        ],
      });
      if (samplesRef.current) {
        samplesRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    setSubmitting(true);

    try {
      const usableSamples = samples.filter((sample) => sample.file);
      const response = await registerVoiceTalent({
        answers,
        samples: usableSamples,
      });
      setSuccess(response.message || t("Your registration has been received."));
      setAnswers({});
      setSamples([emptySample()]);
    } catch (error) {
      if (error.response && error.response.status === 422) {
        setErrors(error.response.data.errors || {});
      } else {
        setErrors({ general: [t("Something went wrong. Please try again.")] });
      }
      setTimeout(focusErrors, 50);
    } finally {
      setSubmitting(false);
    }
  };

  const errorFor = (key) => errors[`answers.${key}`];

  // Server-side sample errors are keyed by position among the uploaded files.
  const uploadIndexOf = (uid) =>
    samples.filter((sample) => sample.file).findIndex((sample) => sample.uid === uid);

  const sampleErrorFor = (uid) => {
    const index = uploadIndexOf(uid);
    if (index < 0) return null;
    return errors[`samples.${index}`] || null;
  };

  const renderField = (field) => {
    if (SKIP_TYPES.includes(field.type)) return null;

    const label = field.label[lng];
    const placeholder = field.placeholder ? field.placeholder[lng] : "";
    const value = answers[field.key] ?? (CHOICE_MULTI.includes(field.type) ? [] : "");

    return (
      <div key={field.key} className="flex flex-col gap-[6px]">
        <label className="text-purple font-medium">
          {label}
          {field.required && <span className="text-orange"> *</span>}
        </label>

        {field.type === "textarea" && (
          <textarea
            value={value}
            placeholder={placeholder}
            onChange={(e) => setAnswer(field.key, e.target.value)}
            className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none min-h-[100px]"
          />
        )}

        {["text", "email", "phone", "number", "date"].includes(field.type) && (
          <input
            type={
              field.type === "email"
                ? "email"
                : field.type === "number"
                ? "number"
                : field.type === "phone"
                ? "tel"
                : field.type === "date"
                ? "date"
                : "text"
            }
            value={value}
            placeholder={placeholder}
            onChange={(e) => setAnswer(field.key, e.target.value)}
            className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none"
          />
        )}

        {CHOICE_SINGLE.includes(field.type) && (
          <SearchableSelect
            value={value}
            onChange={(nextValue) => setAnswer(field.key, nextValue)}
            placeholder={placeholder || t("Select")}
            options={field.options.map((option) => ({
              value: option.value,
              label: option.label[lng],
            }))}
          />
        )}

        {CHOICE_MULTI.includes(field.type) && (
          <div className="flex flex-wrap gap-[12px]">
            {field.options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-[6px] text-gray cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => toggleMulti(field.key, option.value)}
                />
                {option.label[lng]}
              </label>
            ))}
          </div>
        )}

        {errorFor(field.key) && (
          <span className="text-orange text-sm">{errorFor(field.key)[0]}</span>
        )}
      </div>
    );
  };

  if (loading) return <Loader />;

  return (
    <div dir={lng === "en" ? "ltr" : "rtl"} className="overflow-hidden">
      <div className="container mx-auto mt-[20px] mb-[140px] flex flex-col gap-[30px]">
        <Title title={t("Join as a Voice Talent")} />

        {success ? (
          <div className="bg-silver text-purple rounded-xl p-[30px] text-center text-lg">
            {success}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-[20px] max-w-[720px]"
          >
            {errorList.length > 0 && (
              <div
                ref={errorBoxRef}
                className="border-[1px] border-orange bg-orange/5 rounded-lg p-[16px] flex flex-col gap-[6px]"
              >
                <span className="text-orange font-semibold">
                  {t("Please fix the following:")}
                </span>
                <ul className="flex flex-col gap-[4px] list-disc ps-[20px]">
                  {errorList.map((message, index) => (
                    <li key={index} className="text-orange text-sm">
                      {message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {fields.map(renderField)}

            <div
              ref={samplesRef}
              className="flex flex-col gap-[12px] border-t-[1px] border-purpleLight pt-[20px]"
            >
              <h3 className="text-purple font-semibold text-lg">
                {t("Voice Samples")}
              </h3>

              {selectedPerformanceTypes.length > 0 && (
                <div className="bg-silver rounded-lg p-[16px] flex flex-col gap-[8px]">
                  <p className="text-purple text-sm font-medium">
                    {t(
                      "A voice sample is required for each performance type you selected, or remove the type."
                    )}
                  </p>
                  <ul className="flex flex-wrap gap-x-[18px] gap-y-[6px]">
                    {selectedPerformanceTypes.map((value) => {
                      const covered = coveredTypes.has(value);
                      return (
                        <li
                          key={value}
                          className={`flex items-center gap-[6px] text-sm ${
                            covered ? "text-purple" : "text-orange"
                          }`}
                        >
                          <span>{covered ? "✓" : "•"}</span>
                          <span>{performanceLabel(value)}</span>
                          {!covered && (
                            <span className="text-orange">
                              — {t("Sample required")}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {samples.length === 0 && (
                <p className="text-gray text-sm">{t("No samples added yet.")}</p>
              )}

              {samples.map((sample, index) => {
                const sampleError = sampleErrorFor(sample.uid);
                const needsType =
                  Boolean(sample.file) &&
                  !sample.performanceType &&
                  selectedPerformanceTypes.length > 0;

                return (
                  <div
                    key={sample.uid}
                    className="flex flex-col gap-[10px] border-[1px] border-purpleLight rounded-lg p-[16px]"
                  >
                    <div className="flex items-center justify-between gap-[12px]">
                      <span className="text-purple font-medium">
                        {t("Sample")} {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSample(sample.uid)}
                        className="text-orange text-sm underline hover:no-underline"
                      >
                        {t("Remove sample")}
                      </button>
                    </div>

                    <div className="flex flex-col gap-[4px]">
                      <label className="text-gray text-sm">
                        {t("Audio file")}
                      </label>
                      <div className="flex flex-wrap items-center gap-[10px]">
                        <input
                          key={`${sample.uid}-${sample.fileNonce}`}
                          type="file"
                          accept={fileAccept}
                          onChange={(e) =>
                            handleSampleFile(
                              sample.uid,
                              e.target.files[0] || null
                            )
                          }
                        />
                        {sample.file && (
                          <button
                            type="button"
                            onClick={() => clearSampleFile(sample.uid)}
                            className="text-orange text-sm underline hover:no-underline"
                          >
                            {t("Remove file")}
                          </button>
                        )}
                      </div>
                      <span className="text-gray text-xs">{sizeHint}</span>
                      {sampleIssues[sample.uid] && (
                        <span className="text-orange text-sm">
                          {sampleIssues[sample.uid]}
                        </span>
                      )}
                    </div>

                    <input
                      type="text"
                      placeholder={t("Sample title")}
                      value={sample.title}
                      onChange={(e) =>
                        updateSample(sample.uid, { title: e.target.value })
                      }
                      className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none"
                    />

                    {samplePerformanceOptions.length > 0 && (
                      <div className="flex flex-col gap-[4px]">
                        <label className="text-gray text-sm">
                          {t("Performance Type")}
                          {selectedPerformanceTypes.length > 0 && (
                            <span className="text-orange"> *</span>
                          )}
                        </label>
                        <SearchableSelect
                          value={sample.performanceType}
                          onChange={(nextValue) =>
                            updateSample(sample.uid, {
                              performanceType: nextValue,
                            })
                          }
                          placeholder={t("Performance Type")}
                          options={samplePerformanceOptions}
                        />
                        {needsType && (
                          <span className="text-orange text-sm">
                            {t("Choose the performance type for this sample.")}
                          </span>
                        )}
                      </div>
                    )}

                    {sampleError && (
                      <span className="text-orange text-sm">
                        {Array.isArray(sampleError)
                          ? sampleError[0]
                          : sampleError}
                      </span>
                    )}
                  </div>
                );
              })}

              <button
                type="button"
                onClick={addSample}
                className="self-start text-purpleLight underline hover:no-underline"
              >
                {samples.length === 0
                  ? t("Add sample")
                  : t("Add another sample")}
              </button>
            </div>

            {missingTypes.length > 0 && (
              <div className="border-[1px] border-orange bg-orange/5 rounded-lg p-[12px] text-orange text-sm">
                {t("Missing samples for:")}{" "}
                {missingTypes.map(performanceLabel).join("، ")}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="self-start bg-purple text-white rounded-full px-[32px] py-[12px] hover:bg-purpleLight transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t("Submitting...") : t("Submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegisterVoiceTalent;
